/**
 * DelegationListItem.tsx — Single delegation card for inbox.
 * 
 * Shows:
 * - Source object (name + type badge)
 * - Delegator / Delegate
 * - Status badge
 * - Due date (with overdue indicator)
 * - Click to navigate to detail page
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { DelegationSummary } from '../../types/delegation';
import './DelegationListItem.scss';

interface DelegationListItemProps {
  delegation: DelegationSummary;
  showDelegator?: boolean; // If true, show "Delegated by X" instead of "Delegated to Y"
}

export const DelegationListItem: React.FC<DelegationListItemProps> = ({
  delegation,
  showDelegator = false,
}) => {
  const navigate = useNavigate();

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Active':
        return 'status-active';
      case 'Completed':
        return 'status-completed';
      case 'Recalled':
        return 'status-recalled';
      case 'Cancelled':
        return 'status-cancelled';
      default:
        return 'status-unknown';
    }
  };

  const handleClick = () => {
    // PendingUpdateSummary items carry delegationGuid (parent delegation) — always prefer that.
    // DelegationSummary items only have guid.
    const guid = (delegation as any).delegationGuid || (delegation as any).guid;
    navigate(`/delegations/${guid}`);
  };

  return (
    <div className="delegation-list-item" onClick={handleClick}>
      <div className="item-header">
        <div className="source-object">
          <span className="object-type">{delegation.sourceObjectType}</span>
          <span className="object-name">{delegation.sourceObjectName || delegation.sourceObjectId}</span>
        </div>
        <div className="status-section">
          <span className={`status-badge ${getStatusColor(delegation.status)}`}>
            {delegation.status}
          </span>
        </div>
      </div>

      <div className="item-details">
        <div className="person-info">
          {showDelegator ? (
            <span className="label">
              Delegated by <strong>{delegation.delegatedByDisplayName}</strong>
            </span>
          ) : (
            <span className="label">
              Delegated to <strong>{delegation.delegatedToDisplayName}</strong>
            </span>
          )}
        </div>

        {delegation.dueDate && (
          <div className="due-date">
            <span className={`date ${delegation.isOverdue ? 'overdue' : ''}`}>
              Due: {new Date(delegation.dueDate).toLocaleDateString()}
              {delegation.isOverdue && <span className="overdue-badge">OVERDUE</span>}
            </span>
          </div>
        )}

        {delegation.note && (
          <div className="note">
            <p>{delegation.note.substring(0, 100)}...</p>
          </div>
        )}
      </div>

      <div className="item-footer">
        <span className="created-date">
          Created {new Date((delegation as any).createDt ?? (delegation as any).requestedAt).toLocaleDateString()}
        </span>
        <span className="click-hint">→</span>
      </div>
    </div>
  );
};

export default DelegationListItem;
